/**
 * Loading Dialog Component
 * Shows a modal dialog with loading animation for async operations
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";

interface LoadingOptions {
  title: string;
  message: string;
}

interface LoadingState extends LoadingOptions {
  isOpen: boolean;
}

interface LoadingContextType {
  showLoading: (options: LoadingOptions) => void;
  hideLoading: () => void;
  withLoading: <T>(options: LoadingOptions, fn: () => Promise<T>) => Promise<T>;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

interface LoadingProviderProps {
  children: ReactNode;
}

export function LoadingProvider({ children }: LoadingProviderProps) {
  const [state, setState] = useState<LoadingState>({
    isOpen: false,
    title: "",
    message: "",
  });

  const showLoading = useCallback((options: LoadingOptions) => {
    setState({
      isOpen: true,
      title: options.title,
      message: options.message,
    });
  }, []);

  const hideLoading = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const withLoading = useCallback(
    async <T,>(options: LoadingOptions, fn: () => Promise<T>): Promise<T> => {
      showLoading(options);
      try {
        const result = await fn();
        return result;
      } finally {
        hideLoading();
      }
    },
    [showLoading, hideLoading],
  );

  return (
    <LoadingContext.Provider value={{ showLoading, hideLoading, withLoading }}>
      {children}
      <Dialog
        open={state.isOpen}
        onOpenChange={(open) => !open && hideLoading()}
      >
        <DialogContent
          className="sm:max-w-md bg-card border-border"
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>{state.title}</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {state.message}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
          </div>
        </DialogContent>
      </Dialog>
    </LoadingContext.Provider>
  );
}

export function useLoadingDialog() {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error("useLoadingDialog must be used within a LoadingProvider");
  }
  return context;
}
